import {
    Check,
    Wrench,
    GraduationCap,
    Music,
    Building2,
    Landmark,
    Users,
    Briefcase,
    Zap,
    LifeBuoy,
    ArrowRight,
    AlertTriangle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { SectionNumber } from "../visuals/SectionNumber";

const included = [
    { icon: Zap, text: "90-day pilot program at no cost" },
    { icon: Wrench, text: "Professional installation support" },
    { icon: GraduationCap, text: "Device onboarding and staff education materials" },
    { icon: LifeBuoy, text: "Routine maintenance and technical support" },
    { icon: Building2, text: "Deployment in mutually agreed-upon locations" },
    { icon: Users, text: "Ongoing communication with the Kairos team" },
];

const audience = [
    { icon: GraduationCap, label: "Schools & Universities" },
    { icon: Music, label: "Nightlife Venues" },
    { icon: Users, label: "Community Centers" },
    { icon: Landmark, label: "Municipal Facilities" },
    { icon: Briefcase, label: "Workplaces" },
    { icon: Building2, label: "Public Gathering Spaces" },
];

const benefits = [
    "Early access to emerging overdose response technology",
    "Dedicated support from the Kairos team",
    "The opportunity to influence product development",
    "Real-world evaluation of overdose preparedness within your facility",
    "Recognition as an organization helping advance innovative public safety solutions",
];

const goToContact = () => {
    // no longer used — kept for backwards compat
    window.dispatchEvent(
        new CustomEvent("kairos:set-inquiry", { detail: "demo" })
    );
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
};

export const Pilot = () => {
    return (
        <section
            id="pilot"
            data-testid="pilot-section"
            className="relative bg-white py-28 md:py-36 border-y border-slate-200"
        >
            <SectionNumber n={7} total={12} />
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                {/* Header */}
                <div className="reveal max-w-3xl">
                    <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                        Pilot Program
                    </span>
                    <h2 className="mt-6 font-display font-extrabold text-4xl md:text-6xl leading-[0.98] tracking-tighter text-[#0A1428] text-balance">
                        Help shape the future
                        <br />
                        of overdose response.
                    </h2>
                </div>

                {/* Intro */}
                <div className="reveal mt-12 grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-8">
                    <div className="lg:col-span-8 space-y-5 text-lg text-slate-600 leading-relaxed font-medium">
                        <p>
                            Kairos ORD is partnering with organizations to
                            evaluate a new standard in emergency overdose
                            preparedness through a{" "}
                            <span className="text-[#0A1428] font-bold">
                                no-cost, 90-day pilot program
                            </span>
                            .
                        </p>
                        <p>
                            Our mission is simple: make over-the-counter
                            Naloxone as visible and accessible as{" "}
                            <span className="text-[#0A1428] font-bold">
                                fire extinguishers and AEDs
                            </span>
                            {" "}— so communities have reliable access to
                            emergency supplies when they want them.
                        </p>
                        <p>
                            Whether you&apos;re a school, nightlife venue,
                            workplace, community organization, or public
                            facility, becoming a pilot partner gives you the
                            opportunity to help shape the future of overdose
                            response while strengthening safety for your
                            community.
                        </p>
                    </div>
                    <div className="lg:col-span-4 flex lg:justify-end">
                        <div className="bg-[#0A1428] text-white p-6 md:p-8 max-w-sm w-full">
                            <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                                At A Glance
                            </div>
                            <div className="mt-4 space-y-3 font-display font-bold text-lg tracking-tight">
                                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                                    <span className="text-slate-400 text-sm font-medium">
                                        Length
                                    </span>
                                    <span className="text-white">90 days</span>
                                </div>
                                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                                    <span className="text-slate-400 text-sm font-medium">
                                        Cost to partner
                                    </span>
                                    <span className="text-[#F0493E]">$0</span>
                                </div>
                                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                                    <span className="text-slate-400 text-sm font-medium">
                                        Commitment
                                    </span>
                                    <span className="text-white text-base">
                                        None after pilot
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-slate-400 text-sm font-medium">
                                        Availability
                                    </span>
                                    <span className="text-white text-base">
                                        Now enrolling
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* What the Pilot Includes */}
                <div className="mt-24">
                    <div className="reveal max-w-2xl">
                        <div className="text-xs font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            What the Pilot Includes
                        </div>
                        <h3 className="mt-4 font-display font-extrabold text-3xl md:text-4xl leading-[1.02] tracking-tighter text-[#0A1428]">
                            Everything you need — nothing you don&apos;t.
                        </h3>
                    </div>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                        {included.map((it, i) => (
                            <div
                                key={it.text}
                                data-testid={`pilot-includes-${i}`}
                                className="reveal bg-white p-6 md:p-8 flex items-start gap-4 hover:bg-slate-50 transition-colors"
                            >
                                <div className="shrink-0 h-11 w-11 grid place-items-center border border-[#0A1428]">
                                    <it.icon
                                        size={18}
                                        strokeWidth={1.5}
                                        className="text-[#F0493E]"
                                    />
                                </div>
                                <p className="font-display font-bold text-base md:text-lg text-[#0A1428] leading-snug tracking-tight">
                                    {it.text}
                                </p>
                            </div>
                        ))}
                    </div>
                    <p className="reveal mt-6 text-sm md:text-base text-slate-500 font-medium leading-relaxed max-w-3xl">
                        There is no long-term commitment. At the end of the
                        pilot, we&apos;ll review the results together and
                        discuss whether a permanent partnership makes sense
                        for your organization.
                    </p>
                </div>

                {/* Who We're Looking For */}
                <div className="mt-24">
                    <div className="reveal max-w-2xl">
                        <div className="text-xs font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            Who We&apos;re Looking For
                        </div>
                        <h3 className="mt-4 font-display font-extrabold text-3xl md:text-4xl leading-[1.02] tracking-tighter text-[#0A1428]">
                            Organizations ready to raise the bar
                            <br />
                            on emergency preparedness.
                        </h3>
                    </div>
                    <div className="reveal mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        {audience.map((a, i) => (
                            <div
                                key={a.label}
                                data-testid={`pilot-audience-${i}`}
                                className="group flex flex-col items-center text-center bg-slate-50 border border-slate-200 py-6 px-4 hover:border-[#F0493E] hover:bg-white transition-colors"
                            >
                                <a.icon
                                    size={22}
                                    strokeWidth={1.5}
                                    className="text-[#0A1428] group-hover:text-[#F0493E] transition-colors"
                                />
                                <span className="mt-4 font-display font-bold text-sm md:text-base text-[#0A1428] tracking-tight leading-tight">
                                    {a.label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Why Participate + Our Commitment */}
                <div className="mt-24 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div
                        data-testid="pilot-benefits"
                        className="reveal bg-slate-50 border border-slate-200 p-8 md:p-10"
                    >
                        <div className="text-xs font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            Why Participate
                        </div>
                        <h3 className="mt-4 font-display font-extrabold text-2xl md:text-3xl leading-[1.05] tracking-tighter text-[#0A1428]">
                            What you get as a pilot partner.
                        </h3>
                        <ul className="mt-6 space-y-4">
                            {benefits.map((b) => (
                                <li
                                    key={b}
                                    className="flex items-start gap-3 text-sm md:text-[15px] text-slate-700 leading-relaxed font-medium"
                                >
                                    <Check
                                        size={18}
                                        strokeWidth={2}
                                        className="mt-0.5 text-[#F0493E] shrink-0"
                                    />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div
                        data-testid="pilot-commitment"
                        className="reveal bg-[#0A1428] text-white p-8 md:p-10 flex flex-col"
                    >
                        <div className="text-xs font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            Our Commitment
                        </div>
                        <h3 className="mt-4 font-display font-extrabold text-2xl md:text-3xl leading-[1.05] tracking-tighter text-white">
                            Faster. Simpler.
                            <br />
                            More accessible.
                        </h3>
                        <p className="mt-6 text-sm md:text-[15px] text-slate-300 leading-relaxed font-medium">
                            Kairos ORD is committed to making overdose response
                            faster, simpler, and more accessible. Our pilot
                            program is designed to learn alongside our
                            partners, gather meaningful feedback, and continue
                            building technology that helps communities respond
                            with confidence when the moment matters most.
                        </p>

                        <div className="mt-8 pt-6 border-t border-white/10 flex items-start gap-3">
                            <AlertTriangle
                                size={16}
                                strokeWidth={1.5}
                                className="mt-0.5 text-[#F0493E] shrink-0"
                            />
                            <p className="text-xs md:text-[13px] text-slate-400 leading-relaxed font-medium">
                                <span className="text-white font-bold">
                                    Emergency notice:
                                </span>{" "}
                                During any suspected overdose or medical
                                emergency, always call 911 immediately and
                                follow your organization&apos;s emergency
                                response procedures.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Final CTA */}
                <div
                    data-testid="pilot-cta"
                    className="reveal mt-20 relative bg-[#0A1428] text-white overflow-hidden"
                >
                    <div className="absolute inset-0 dot-grid-light opacity-25 pointer-events-none" />
                    <div className="absolute -top-24 -right-24 w-[420px] h-[420px] opacity-[0.08] pointer-events-none">
                        <svg viewBox="0 0 200 200">
                            <polygon
                                points="20,160 130,60 180,160"
                                fill="#F0493E"
                            />
                        </svg>
                    </div>

                    <div className="relative p-8 md:p-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                        <div className="max-w-2xl">
                            <div className="text-[10px] md:text-xs font-bold uppercase tracking-[0.32em] text-[#F0493E]">
                                Become a Pilot Partner
                            </div>
                            <h3 className="mt-4 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-balance">
                                Bring Kairos ORD
                                <br />
                                to your organization.
                            </h3>
                            <p className="mt-6 max-w-lg text-base md:text-lg text-slate-300 font-medium leading-relaxed">
                                Complete the form and a member of our team
                                will contact you to discuss your facility,
                                answer your questions, and determine whether
                                your organization is a good fit for the pilot
                                program.
                            </p>
                        </div>
                        <Link
                            to="/pilot-application"
                            data-testid="pilot-cta-button"
                            className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm self-start md:self-end"
                        >
                            Apply to the pilot
                            <ArrowRight size={18} strokeWidth={2} />
                        </Link>
                    </div>

                    <div className="relative px-8 md:px-14 pb-8 md:pb-10 border-t border-white/10 pt-6 md:pt-8">
                        <div className="font-display font-extrabold text-xl md:text-2xl tracking-tight text-white/90">
                            Built for the moments that matter.
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
